/* hdnywholesale — saved lists

   Lists live in localStorage for now. Everything that touches storage is
   funnelled through load() and save() so swapping in a hosted store later
   is a two-function change, not a rewrite.

   Design codes resolve against an index rendered by Liquid, because
   Shopify's search does not index metafields — pasting D262 into the
   search endpoint returns nothing. Prices and stock still come from the
   live product JSON, so nothing shown here is a stale copy.

   Pricing: variant.price IS the wholesale rate. Line totals and the dock
   total are the price as Shopify holds it, with no divisor and no
   discount arithmetic, so the figure here matches the cart and checkout.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-hdny-lists]');
  if (!root) return;

  var KEY = 'hdny-saved-lists-v1';
  var pack = parseInt(root.dataset.pack, 10) || 1;
  var signedIn = root.dataset.signedIn === 'true';
  var money = root.dataset.moneyFormat || '${{amount}}';
  var freeFreight = parseInt(root.dataset.freeFreight, 10) || 0;

  var state = { lists: [], activeId: null };
  var cache = {};

  var CODES = {};
  try {
    var idxEl = document.querySelector('[data-hdny-code-index]');
    if (idxEl) CODES = JSON.parse(idxEl.textContent) || {};
  } catch (e) { CODES = {}; }

  function fmt(cents) {
    return money.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2))
                .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function uid() { return 'l' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }

  /* A buyer may type D262, d262 or 262 — all mean the same design. */
  function lookup(raw) {
    var k = String(raw).trim().toUpperCase();
    if (CODES[k]) return { code: k, hit: CODES[k] };
    var bare = k.replace(/^D/, '');
    if (CODES[bare]) return { code: bare, hit: CODES[bare] };
    if (CODES['D' + k]) return { code: 'D' + k, hit: CODES['D' + k] };
    return null;
  }

  /* ---- the only two functions that know where lists live ---- */
  function load() {
    try {
      var raw = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (raw && Array.isArray(raw.lists) && raw.lists.length) return raw;
    } catch (e) {}
    return { lists: [{ id: uid(), name: 'Monthly restock', items: [], updated: Date.now() }], activeId: null };
  }
  function save() {
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
  }

  function active() {
    return state.lists.filter(function (l) { return l.id === state.activeId; })[0] || state.lists[0];
  }

  function fetchProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { if (!r.ok) throw new Error('404'); return r.json(); })
      .then(function (p) { cache[handle] = p; return p; });
  }

  function hydrate(list) {
    return Promise.all(list.items.map(function (it) {
      return fetchProduct(it.handle).then(function (p) {
        var v = p.variants.filter(function (x) { return x.id === it.variantId; })[0];
        return v ? { it: it, p: p, v: v } : null;
      }).catch(function () { return null; });
    })).then(function (rows) { return rows.filter(Boolean); });
  }

  /* ---- rendering ---- */
  var railEl = root.querySelector('[data-hdny-rail]');
  var bodyEl = root.querySelector('[data-hdny-body]');
  var titleEl = root.querySelector('[data-hdny-title]');
  var metaEl = root.querySelector('[data-hdny-meta]');
  var dockEl = root.querySelector('[data-hdny-dock]');
  var totEl = root.querySelector('[data-hdny-total]');
  var pcsEl = root.querySelector('[data-hdny-pcs]');
  var noteEl = root.querySelector('[data-hdny-note]');
  var skipEl = root.querySelector('[data-hdny-skip]');
  var addEl = root.querySelector('[data-hdny-addall]');

  function renderRail() {
    railEl.innerHTML = state.lists.map(function (l) {
      var n = l.items.length;
      return '<li><button type="button" data-hdny-pick="' + l.id + '"' +
        (l.id === state.activeId ? ' class="is-on"' : '') + '>' +
        '<b>' + esc(l.name) + '</b>' +
        '<span>' + n + (n === 1 ? ' design' : ' designs') + '</span>' +
        '</button></li>';
    }).join('');
  }

  function renderBody() {
    var list = active();
    if (!list) return;
    titleEl.textContent = list.name;

    if (!list.items.length) {
      metaEl.textContent = 'Nothing saved yet';
      bodyEl.innerHTML =
        '<div class="hdny-sl__empty">' +
          '<h3>This list is empty</h3>' +
          '<p>Paste design codes into the box above, or open any product and add it from there.</p>' +
          '<a class="hdny-btn hdny-btn--go" href="/collections/all-products">Browse the catalogue</a>' +
        '</div>';
      dockEl.classList.remove('is-on');
      return;
    }

    bodyEl.innerHTML = '<p class="hdny-sl__empty" style="padding:40px;text-align:center;color:var(--hdny-slate)">Loading\u2026</p>';

    hydrate(list).then(function (rows) {
      var pcs = 0, cents = 0, designs = rows.length;
      var skip = skipEl && skipEl.checked;

      bodyEl.innerHTML = rows.map(function (r) {
        /* Straight from Shopify — this is already the wholesale rate. */
        var cost = r.v.price;
        var qty = r.it.qty == null ? pack : r.it.qty;
        var lineCents = cost * qty;
        var counts = r.v.available || !skip;
        if (counts) { pcs += qty; cents += lineCents; }

        var stock = r.v.available
          ? (r.v.inventory_quantity > 0 && r.v.inventory_quantity <= 25
              ? '<span class="hdny-sl__stk hdny-sl__stk--low">' + r.v.inventory_quantity + ' pcs left</span>'
              : '<span class="hdny-sl__stk">In stock</span>')
          : '<span class="hdny-sl__stk hdny-sl__stk--no">Out of stock</span>';

        var img = r.v.featured_image ? r.v.featured_image.src : (r.p.featured_image || '');

        return '<div class="hdny-sl__row' + (r.v.available ? '' : ' is-out') + '" data-hdny-line="' + r.v.id + '">' +
          '<span class="hdny-sl__thumb">' + (img ? '<img src="' + esc(img) + '" alt="" loading="lazy">' : '') + '</span>' +
          '<span class="hdny-sl__nm">' +
            '<b>' + (r.it.code ? '<span class="hdny-sl__code">D' + esc(String(r.it.code).replace(/^D/i, '')) + '</span>' : '') + esc(r.p.title) + '</b>' +
            '<span class="hdny-sl__var">' + esc(r.v.title) + '</span>' +
          '</span>' +
          stock +
          '<span class="hdny-sl__r hdny-sl__unit">' + (signedIn ? fmt(cost) : '\u2014') + '</span>' +
          '<span class="hdny-sl__qty">' +
            '<button type="button" data-hdny-step="-1">\u2212</button>' +
            '<input type="number" min="0" step="' + pack + '" value="' + qty + '" inputmode="numeric" aria-label="Quantity">' +
            '<button type="button" data-hdny-step="1">+</button>' +
          '</span>' +
          '<span class="hdny-sl__r hdny-sl__line">' + (signedIn ? fmt(lineCents) : '\u2014') + '</span>' +
          '<button type="button" class="hdny-sl__x" data-hdny-drop aria-label="Remove">' +
            '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
          '</button>' +
        '</div>';
      }).join('');

      metaEl.textContent = designs + (designs === 1 ? ' design' : ' designs') + ' \u00b7 ' + pcs + ' pcs';
      totEl.textContent = signedIn ? fmt(cents) : 'Sign in for pricing';
      pcsEl.textContent = pcs + ' pcs \u00b7 ' + designs + (designs === 1 ? ' design' : ' designs');

      if (noteEl) {
        if (!signedIn) {
          noteEl.textContent = 'Sign in to see your cost and order.';
        } else if (freeFreight) {
          noteEl.textContent = cents >= freeFreight
            ? 'Free freight \u2014 this order clears the threshold.'
            : fmt(freeFreight - cents) + ' more for free freight.';
        }
      }

      addEl.textContent = signedIn ? 'Add all to cart' : 'Sign in to order';
      dockEl.classList.add('is-on');
    });
  }

  function render() { renderRail(); renderBody(); }

  /* ---- events ---- */
  railEl.addEventListener('click', function (e) {
    var b = e.target.closest('[data-hdny-pick]');
    if (!b) return;
    state.activeId = b.dataset.hdnyPick;
    save(); render();
  });

  root.querySelectorAll('[data-hdny-new]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var name = window.prompt('Name this list', 'New list');
      if (!name) return;
      var l = { id: uid(), name: name.trim().slice(0, 60), items: [], updated: Date.now() };
      state.lists.push(l);
      state.activeId = l.id;
      save(); render();
    });
  });

  root.querySelector('[data-hdny-rename]').addEventListener('click', function () {
    var l = active(); if (!l) return;
    var name = window.prompt('Rename list', l.name);
    if (!name) return;
    l.name = name.trim().slice(0, 60);
    save(); render();
  });

  root.querySelector('[data-hdny-delete]').addEventListener('click', function () {
    var l = active(); if (!l) return;
    if (!window.confirm('Delete "' + l.name + '"? This cannot be undone.')) return;
    state.lists = state.lists.filter(function (x) { return x.id !== l.id; });
    if (!state.lists.length) state.lists.push({ id: uid(), name: 'Monthly restock', items: [], updated: Date.now() });
    state.activeId = state.lists[0].id;
    save(); render();
  });

  bodyEl.addEventListener('click', function (e) {
    var row = e.target.closest('[data-hdny-line]');
    if (!row) return;
    var vid = parseInt(row.dataset.hdnyLine, 10);
    var l = active();

    if (e.target.closest('[data-hdny-drop]')) {
      l.items = l.items.filter(function (i) { return i.variantId !== vid; });
      save(); render();
      return;
    }
    var step = e.target.closest('[data-hdny-step]');
    if (step) {
      var input = row.querySelector('input');
      var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.hdnyStep, 10) * pack);
      input.value = next;
      l.items.forEach(function (i) { if (i.variantId === vid) i.qty = next; });
      save(); renderBody();
    }
  });

  bodyEl.addEventListener('change', function (e) {
    var row = e.target.closest('[data-hdny-line]');
    if (!row || e.target.tagName !== 'INPUT') return;
    var vid = parseInt(row.dataset.hdnyLine, 10);
    active().items.forEach(function (i) {
      if (i.variantId === vid) i.qty = Math.max(0, parseInt(e.target.value, 10) || 0);
    });
    save(); renderBody();
  });

  /* ---- quick add by design code ---- */
  var codeInput = root.querySelector('[data-hdny-code]');
  var codeHint = root.querySelector('[data-hdny-code-hint]');

  function addCodes() {
    var raw = (codeInput.value || '').trim();
    if (!raw) return;

    var codes = raw.split(/[\s,]+/).filter(Boolean);
    var l = active();
    var added = 0, missing = [], dupes = 0;

    codes.forEach(function (c) {
      var found = lookup(c);
      if (!found) { missing.push(c.toUpperCase()); return; }
      if (l.items.some(function (i) { return i.variantId === found.hit.variantId; })) { dupes++; return; }
      l.items.push({
        handle: found.hit.handle,
        variantId: found.hit.variantId,
        qty: pack,
        code: found.code
      });
      added++;
    });

    l.updated = Date.now();
    save(); render();
    codeInput.value = '';

    var msg = 'Added ' + added + ' to ' + l.name + '.';
    if (dupes) msg += ' ' + dupes + ' already on the list.';
    if (missing.length) {
      msg += ' Not found: ' + missing.join(', ') + '.';
      codeHint.className = 'hdny-sl__hint hdny-sl__hint--bad';
    } else {
      codeHint.className = 'hdny-sl__hint';
    }
    codeHint.textContent = msg;
  }

  root.querySelector('[data-hdny-code-go]').addEventListener('click', addCodes);
  codeInput.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') { e.preventDefault(); addCodes(); }
  });

  /* ---- add all to cart ---- */
  addEl.addEventListener('click', function () {
    if (!signedIn) { window.location.href = '/pages/account-login'; return; }
    var l = active();
    var skip = skipEl && skipEl.checked;

    hydrate(l).then(function (rows) {
      var items = rows.filter(function (r) {
        if (skip && !r.v.available) return false;
        return (r.it.qty || 0) > 0;
      }).map(function (r) { return { id: r.v.id, quantity: r.it.qty }; });

      if (!items.length) return;

      addEl.disabled = true;
      addEl.textContent = 'Adding\u2026';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body.description || 'Could not add');
          addEl.textContent = 'Added';
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (!cart) return;
          document.querySelectorAll('.cart-bubble__text, [data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
          });
        })
        .catch(function (err) { addEl.textContent = String(err.message).slice(0, 36); })
        .finally(function () {
          setTimeout(function () { addEl.disabled = false; addEl.textContent = 'Add all to cart'; }, 1500);
        });
    });
  });

  if (skipEl) skipEl.addEventListener('change', renderBody);

  /* ---- boot ---- */
  state = load();
  if (!state.activeId || !state.lists.some(function (l) { return l.id === state.activeId; })) {
    state.activeId = state.lists[0].id;
  }
  render();
})();
